/**
 * Validador de escopo de acesso do usuário
 */

import { supabase } from '@/integrations/supabase/client';

export type ScopeType = 'ALL_STORES' | 'STORE_SET' | 'SINGLE_STORE';

export interface UserScope {
  scopeType: ScopeType;
  storeIds: string[];
  networkId: string;
}

export interface UserNetwork {
  networkId: string;
  networkName: string;
}

/**
 * Obtém a rede única do usuário
 * IMPORTANTE: Cada usuário deve pertencer a EXATAMENTE uma rede
 * NOTA: Usuário pode ter múltiplas linhas em user_network_roles (uma por loja)
 */
export const getUserNetwork = async (userId: string): Promise<UserNetwork | null> => {
  try {
    const { data: roles, error } = await supabase
      .from('user_network_roles')
      .select('network_id, networks!fk_user_network_roles_network(id, name, is_active)')
      .eq('user_id', userId)
      .eq('is_active', true)
      // Multi-vínculo: redes onde o usuário é só Corretor Parceiro não definem a rede operacional
      .neq('role', 'BROKER_PARTNER');

    if (error || !roles || roles.length === 0) {
      console.warn('Usuário sem rede:', { userId });
      return null;
    }

    // Filtrar apenas redes ativas e deduplicar por network_id
    const uniqueNetworks = new Map<string, { id: string; name: string; is_active: boolean }>();
    
    for (const role of roles) {
      const network = role.networks as { id: string; name: string; is_active: boolean } | null;
      if (network?.is_active && !uniqueNetworks.has(network.id)) {
        uniqueNetworks.set(network.id, network);
      }
    }

    if (uniqueNetworks.size === 0) {
      console.warn('Usuário sem rede ativa:', { userId });
      return null;
    }

    // VALIDAÇÃO CRÍTICA: Usuário deve ter exatamente 1 rede ativa (única)
    if (uniqueNetworks.size > 1) {
      console.error('ERRO DE CONFIGURAÇÃO: Usuário tem múltiplas redes ativas', {
        userId,
        networks: Array.from(uniqueNetworks.keys()),
      });
      throw new Error('Configuração inválida: usuário pertence a múltiplas redes');
    }

    const network = Array.from(uniqueNetworks.values())[0];
    return {
      networkId: network.id,
      networkName: network.name,
    };
  } catch (error) {
    console.error('Erro ao obter rede do usuário:', error);
    return null;
  }
};

/**
 * Obtém o escopo de acesso do usuário em uma rede
 * NOTA: Usuário pode ter múltiplas linhas (uma por loja) - calcula escopo efetivo
 */
export const getUserScope = async (userId: string, networkId: string): Promise<UserScope | null> => {
  try {
    // Verificar se a rede está ativa
    const { data: network, error: networkError } = await supabase
      .from('networks')
      .select('is_active')
      .eq('id', networkId)
      .single();

    if (networkError || !network?.is_active) {
      console.warn('Rede inativa ou não encontrada:', { networkId });
      return null;
    }

    // Buscar TODAS as roles ativas do usuário na rede (pode ter várias)
    const { data: networkRoles, error: roleError } = await supabase
      .from('user_network_roles')
      .select('scope_type, store_ids')
      .eq('user_id', userId)
      .eq('network_id', networkId)
      .eq('is_active', true);

    if (roleError || !networkRoles || networkRoles.length === 0) {
      console.warn('Usuário sem role na rede:', { userId, networkId });
      return null;
    }

    // Calcular escopo efetivo combinando todas as roles
    // Se qualquer role tem ALL_STORES, o escopo efetivo é ALL_STORES
    const hasAllStores = networkRoles.some(r => r.scope_type === 'ALL_STORES');
    if (hasAllStores) {
      return {
        scopeType: 'ALL_STORES',
        storeIds: [],
        networkId,
      };
    }

    // Combinar todos os store_ids únicos
    const allStoreIds = new Set<string>();
    for (const role of networkRoles) {
      if (role.store_ids && Array.isArray(role.store_ids)) {
        role.store_ids.forEach((id: string) => allStoreIds.add(id));
      }
    }

    const uniqueStoreIds = Array.from(allStoreIds);
    
    // Determinar tipo de escopo baseado na quantidade de lojas
    const scopeType: ScopeType = uniqueStoreIds.length === 1 ? 'SINGLE_STORE' : 'STORE_SET';

    return {
      scopeType,
      storeIds: uniqueStoreIds,
      networkId,
    };
  } catch (error) {
    console.error('Erro ao obter escopo do usuário:', error);
    return null;
  }
};

/**
 * Valida se o usuário tem acesso a uma loja específica
 */
export const validateStoreAccess = async (
  userId: string,
  networkId: string,
  storeId: string
): Promise<boolean> => {
  try {
    const scope = await getUserScope(userId, networkId);
    if (!scope) return false;

    // ALL_STORES: tem acesso a todas as lojas
    if (scope.scopeType === 'ALL_STORES') {
      return true;
    }

    // STORE_SET ou SINGLE_STORE: verificar se a loja está no array
    return scope.storeIds.includes(storeId);
  } catch (error) {
    console.error('Erro ao validar acesso à loja:', error);
    return false;
  }
};

/**
 * Valida se o usuário pode OPERAR na loja (tem acesso E a loja está ativa).
 * Regra estrutural: loja desativada não participa de novas operações.
 */
export const validateStoreOperable = async (
  userId: string,
  networkId: string,
  storeId: string
): Promise<boolean> => {
  try {
    const hasAccess = await validateStoreAccess(userId, networkId, storeId);
    if (!hasAccess) return false;

    const { data: store, error } = await supabase
      .from('stores')
      .select('is_active')
      .eq('id', storeId)
      .maybeSingle();

    if (error) throw error;
    return !!store?.is_active;
  } catch (error) {
    console.error('Erro ao validar operabilidade da loja:', error);
    return false;
  }
};

/**
 * Obtém todas as lojas acessíveis pelo usuário em uma rede.
 * Por padrão retorna somente lojas ATIVAS (operacionais).
 * Use includeInactive para consultas históricas.
 */
export const getAccessibleStores = async (
  userId: string,
  networkId: string,
  options?: { includeInactive?: boolean }
): Promise<string[]> => {
  try {
    const includeInactive = options?.includeInactive === true;
    const scope = await getUserScope(userId, networkId);
    if (!scope) return [];

    // ALL_STORES: buscar todas as lojas da rede
    if (scope.scopeType === 'ALL_STORES') {
      let query = supabase
        .from('stores')
        .select('id')
        .eq('network_id', networkId);

      if (!includeInactive) {
        query = query.eq('is_active', true);
      }

      const { data: stores, error } = await query;

      if (error) throw error;
      return stores?.map(s => s.id) || [];
    }

    // STORE_SET ou SINGLE_STORE
    if (includeInactive || scope.storeIds.length === 0) {
      return scope.storeIds;
    }

    // Cruzar os store_ids do escopo com as lojas realmente ativas da rede
    const { data: activeStores, error: activeError } = await supabase
      .from('stores')
      .select('id')
      .eq('network_id', networkId)
      .eq('is_active', true)
      .in('id', scope.storeIds);

    if (activeError) throw activeError;
    return activeStores?.map(s => s.id) || [];
  } catch (error) {
    console.error('Erro ao obter lojas acessíveis:', error);
    return [];
  }
};

/**
 * Valida se o usuário tem acesso a múltiplas lojas
 */
export const hasMultiStoreAccess = async (
  userId: string,
  networkId: string
): Promise<boolean> => {
  try {
    const scope = await getUserScope(userId, networkId);
    if (!scope) return false;

    if (scope.scopeType === 'ALL_STORES') return true;
    return scope.storeIds.length > 1;
  } catch (error) {
    console.error('Erro ao verificar acesso multi-loja:', error);
    return false;
  }
};
